import { useEffect, useMemo, useRef, useState } from 'react';
import { Drawer } from '@/ui/Drawer';
import { Button } from '@/ui/Button';
import { CampoDinamico } from './CampoDinamico';
import {
  estadoDeRango, subirSoportes, useRadicar, useReenviar,
  type CampoDeFormulario, type PasoDelFlujo, type Periodo, type ValorDeCampo,
} from './api';

/**
 * El formulario dinámico: para radicar y para corregir.
 *
 * Los dos casos pintan **exactamente los mismos campos con las mismas reglas**;
 * lo único que cambia es el encabezado, el botón, si los campos llegan vacíos o
 * con lo ya escrito, y si se exigen los soportes. Tener dos formularios
 * distintos habría garantizado que uno se quedara atrás al agregar un tipo de
 * campo.
 *
 * ## La validación
 *
 * La repite el servidor entera. Esta existe para no gastar un viaje y, sobre
 * todo, para poner el mensaje **junto al campo**. La versión vanilla la sacaba
 * por `Utils.toast`: un aviso flotante que se va a los tres segundos mientras
 * el usuario sigue mirando el formulario, y que en un formulario de doce campos
 * no dice cuál.
 *
 * Y había un motivo técnico para que existiera, que sigue vigente: el `required`
 * del `<input>` no lo comprobaba nadie, porque el botón vive en el pie del panel
 * —fuera del `<form>`— y el envío nunca es nativo.
 */
export function FormularioSolicitud({
  abierto, onCerrar, tipo, solicitudId, valoresIniciales, avisoDevolucion, onEnviado,
}: {
  abierto: boolean;
  onCerrar: () => void;
  /** El tipo con sus campos y su flujo. Al corregir, sale del propio detalle
   *  (`r.fields`): no se puede depender del catálogo, porque quien entra directo
   *  a «Mis solicitudes» nunca lo cargó. */
  tipo: { id?: number; name?: string; icon?: string | null; fields?: CampoDeFormulario[]; flow?: PasoDelFlujo[] } | null;
  /** Si viene, se CORRIGE esa solicitud en vez de radicar una nueva. */
  solicitudId?: number;
  valoresIniciales?: Record<string, ValorDeCampo>;
  avisoDevolucion?: string | null;
  onEnviado: (r: { radicado: string; aviso?: string }) => void;
}) {
  const radicar = useRadicar();
  const reenviar = useReenviar();
  const corrigiendo = solicitudId != null;

  const campos = useMemo(() => tipo?.fields ?? [], [tipo]);
  const [valores, setValores] = useState<Record<string, ValorDeCampo>>({});
  const [archivos, setArchivos] = useState<Record<string, File>>({});
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [enviando, setEnviando] = useState(false);

  // Se siembra UNA VEZ POR APERTURA. Es la misma corrección que necesitó Mi
  // ficha el 2026-09-04: sin resembrar al abrir, «Cancelar» no descarta nada;
  // resembrando en cada cambio de `tipo`, un refetch de fondo borra lo que la
  // persona está escribiendo.
  const sembrado = useRef(false);
  useEffect(() => {
    if (!abierto) { sembrado.current = false; return; }
    if (sembrado.current) return;
    setValores({ ...(valoresIniciales ?? {}) });
    setArchivos({});
    setErrores({});
    sembrado.current = true;
  }, [abierto, valoresIniciales]);

  // El guard de navegación del shell. Si alguien cambia de sección con el panel
  // abierto y algo escrito, `app.js` pregunta antes de descartarlo.
  const sucio = Object.keys(archivos).length > 0
    || campos.some((c) => {
      const a = valores[c.key];
      const b = valoresIniciales?.[c.key];
      return JSON.stringify(a ?? '') !== JSON.stringify(b ?? '');
    });
  useEffect(() => {
    window._appFormDirty = abierto && sucio;
    return () => { window._appFormDirty = false; };
  }, [abierto, sucio]);

  /** Las nueve reglas del original, en el mismo orden y con el mismo texto.
   *  Devuelve el mapa de errores; vacío significa que puede salir. */
  function validar(): Record<string, string> {
    const e: Record<string, string> = {};

    for (const f of campos) {
      // Los de autollenado se saltan: el valor lo pone el servidor al recibir.
      if (f.autofill) continue;

      if (f.type === 'file') {
        // Al corregir NO se vuelven a exigir: los soportes ya subidos siguen
        // adjuntos, y volver a pedirlos obligaría a buscar otra vez un archivo
        // que el sistema ya tiene.
        if (f.required && !corrigiendo && !archivos[f.key]) {
          e[f.key] = 'Adjunta este soporte obligatorio.';
        }
        continue;
      }

      if (f.type === 'rango_fechas') {
        const v = valores[f.key] as Periodo | '' | undefined;
        if (!v || typeof v !== 'object') {
          // Vacío del todo: solo molesta si el campo es obligatorio. Media fecha
          // NO llega aquí — `RangoDeFechas` guarda el objeto en cuanto se elige
          // una de las dos, y solo devuelve '' cuando las dos están vacías, así
          // que ese caso lo caza la comprobación de abajo.
          if (f.required) {
            e[f.key] = 'Selecciona la fecha de inicio y la fecha de finalización.';
          }
          continue;
        }
        if (!v.inicio || !v.fin) {
          e[f.key] = 'Selecciona la fecha de inicio y la fecha de finalización.';
          continue;
        }
        // Mismo texto que el bloque de debajo del campo: quien lee el aviso ya
        // vio esa frase, y repetirla con otras palabras confunde.
        const s = estadoDeRango(f, v.inicio, v.fin);
        if (s.estado === 'error') e[f.key] = s.titulo;
        continue;
      }

      if (f.required) {
        const v = valores[f.key];
        const vacio = v === undefined || v === null || v === ''
          || (Array.isArray(v) && v.length === 0);
        if (vacio) e[f.key] = 'Este dato es obligatorio.';
      }
    }

    // Fechas encadenadas. El servidor lo repite; avisar aquí evita un viaje y
    // un mensaje genérico.
    const etiqueta: Record<string, string> = {};
    campos.forEach((f) => { etiqueta[f.key] = f.label; });
    for (const f of campos) {
      if (f.type !== 'date' || !f.date_after) continue;
      const propia = valores[f.key];
      const otra = valores[f.date_after];
      if (typeof propia === 'string' && typeof otra === 'string'
          && propia && otra && propia < otra) {
        e[f.key] = `No puede ser anterior a «${etiqueta[f.date_after] || f.date_after}».`;
      }
    }
    return e;
  }

  async function enviar() {
    const e = validar();
    setErrores(e);
    if (Object.keys(e).length) {
      // El foco al primero que falla. Sin esto, en un formulario de doce campos
      // hay que buscar cuál es el rojo.
      const primero = campos.find((c) => e[c.key]);
      if (primero) {
        const el = document.getElementById(`solf-${primero.key}`)
          ?? document.getElementById(`solf-${primero.key}-inicio`);
        el?.focus();
        el?.scrollIntoView({ block: 'center' });
      }
      return;
    }

    // El período viaja como UN dato; los archivos no van en el cuerpo.
    const form_data: Record<string, ValorDeCampo> = {};
    for (const f of campos) {
      if (f.type === 'file') continue;
      const v = valores[f.key];
      form_data[f.key] = v === undefined || v === null ? '' : v;
    }
    const paraSubir = Object.entries(archivos).map(([key, file]) => ({ key, file }));

    setEnviando(true);
    try {
      if (corrigiendo) {
        await reenviar.mutateAsync({ id: solicitudId!, form_data });
        // Un soporte nuevo puede ser justamente lo que pidieron corregir, así
        // que si falla hay que decirlo: la corrección se envió, pero sin el
        // archivo que la motivaba el aprobador la devolvería otra vez.
        const fallidos = await subirSoportes(solicitudId!, paraSubir, campos);
        onEnviado({ radicado: '', aviso: fallidos.length ? avisoDeSoportes(fallidos) : undefined });
      } else {
        const res = await radicar.mutateAsync({ request_type_id: tipo!.id!, form_data });
        const fallidos = await subirSoportes(res.id, paraSubir, campos);
        onEnviado({
          radicado: res.radicado,
          aviso: fallidos.length ? avisoDeSoportes(fallidos) : res.aviso,
        });
      }
      window._appFormDirty = false;
      onCerrar();
    } catch (err) {
      Utils.toast(err instanceof Error ? err.message : 'No se pudo enviar la solicitud', 'error');
    } finally {
      setEnviando(false);
    }
  }

  const flujo = (tipo?.flow ?? []).map((s) => s.label).filter(Boolean);

  return (
    <Drawer
      abierto={abierto}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo={corrigiendo ? 'Corregir y reenviar' : (tipo?.name ?? 'Nueva solicitud')}
      descripcion={corrigiendo
        ? 'Los soportes que ya adjuntaste siguen adjuntos. Conserva el mismo radicado y su historial.'
        : 'Lo que envíes entra al flujo de aprobación con un radicado propio.'}
      ancho="lg"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button loading={enviando} onClick={() => void enviar()}>
            {corrigiendo ? 'Reenviar solicitud' : 'Enviar solicitud'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {avisoDevolucion !== undefined && (
          <div className="rounded-sm border border-warn/40 bg-warn/12 px-4 py-3">
            <p className="flex items-center gap-2 text-3 font-semibold text-warn-ink">
              <i className="bi bi-info-circle" aria-hidden="true" /> Qué debes corregir
            </p>
            {avisoDevolucion
              ? <p className="mt-1.5 text-3 leading-relaxed text-ink">{avisoDevolucion}</p>
              : <p className="mt-1.5 text-2 text-muted">
                  Quien la devolvió no dejó un motivo escrito.
                </p>}
          </div>
        )}

        {campos.length === 0 && (
          <p className="text-3 text-muted">Este tipo de solicitud no pide ningún dato.</p>
        )}

        {campos.map((c) => (
          <CampoDinamico
            key={c.key}
            campo={c}
            valor={valores[c.key]}
            error={errores[c.key]}
            valorReferido={c.date_after && typeof valores[c.date_after] === 'string'
              ? (valores[c.date_after] as string) : undefined}
            onCambio={(v) => {
              setValores((p) => ({ ...p, [c.key]: v }));
              setErrores((p) => (p[c.key] ? { ...p, [c.key]: '' } : p));
            }}
            onArchivo={(f) => setArchivos((p) => {
              const n = { ...p };
              if (f) n[c.key] = f; else delete n[c.key];
              return n;
            })}
          />
        ))}

        {flujo.length > 0 && (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-sm border border-line
                        bg-sunken px-3 py-2 text-2 text-muted">
            <i className="bi bi-diagram-3" aria-hidden="true" />
            <span className="font-semibold text-ink-2">Flujo de aprobación:</span>
            {flujo.join(' → ')}
          </p>
        )}
      </div>
    </Drawer>
  );
}

/** El aviso cuando un soporte no subió. Dice qué faltó y dónde se arregla: el
 *  radicado sigue siendo bueno, así que mandar al usuario a radicar otra vez
 *  sería el peor consejo posible. */
function avisoDeSoportes(fallidos: string[]): string {
  return `No se pudo adjuntar: ${fallidos.join(', ')}. `
    + 'Puedes subirlo desde «Mis solicitudes» sin volver a radicar.';
}
